/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import * as Sentry from '@sentry/react';
import type { HostedFieldFieldOptions, HostedFields } from 'braintree-web';
import type { HostedFieldEventType, HostedFieldsHostedFieldsFieldData } from 'braintree-web/hosted-fields';
import classnames from 'classnames';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import { braintreeFieldIds } from 'bundles/payments/common/constants';
import { InvalidInputIcon } from 'bundles/payments/lib/validate';
import type PaymentFormStoreType from 'bundles/payments/stores/PaymentFormStore';

import 'css!./__styles__/BraintreeHostedFieldFormGroup';

export type PropsFromCaller = {
  labelText: string;
  subLabelText?: string;
  // key into the HostedFields instance corresponding to the field that this component is used for, e.g. 'cvv'
  braintreeFieldName: keyof HostedFieldFieldOptions;
  cols?: string;
  isFormSubmitted?: boolean;
  isVerticallyAligned?: boolean;
  hideErrorIcon?: boolean;

  onBlur?: (state: HostedFieldsHostedFieldsFieldData) => void;
  onFocus?: (state: HostedFieldsHostedFieldsFieldData) => void;
  onEmpty?: (state: HostedFieldsHostedFieldsFieldData) => void;
  onNotEmpty?: (state: HostedFieldsHostedFieldsFieldData) => void;
  onCardTypeChange?: (state: HostedFieldsHostedFieldsFieldData) => void;
  onValidityChange?: (state: HostedFieldsHostedFieldsFieldData) => void;
  onInputSubmitRequest?: (state: HostedFieldsHostedFieldsFieldData) => void;
};

type PropsFromPaymentsStore = {
  braintreeHostedFields?: HostedFields;
  onStatusChange: () => void;
};

export type PropsToComponent = PropsFromCaller & PropsFromPaymentsStore;

type State = {
  valid: boolean;
  potentiallyValid: boolean;
  focused: boolean;
  attempted: boolean;
};

const styles = {
  formContainer: css`
    .bt3-form-group {
      margin-bottom: 15px;
    }

    .bt3-has-success .bt3-radio {
      color: #3c763d;
    }

    .bt3-has-success .bt3-form-control {
      border-color: #3c763d;
      box-shadow: inset 0 1px 1px rgb(0 0 0 / 7.5%);
    }

    .bt3-has-success .bt3-form-control:focus {
      border-color: #366a37;
      box-shadow: inset 0 1px 1px rgb(0 0 0 / 7.5%), 0 0 6px #52a254;
    }

    .bt3-has-error .bt3-radio {
      color: #a94442;
    }

    .bt3-has-error .bt3-form-control {
      border-color: #a94442;
      box-shadow: inset 0 1px 1px rgb(0 0 0 / 7.5%);
    }

    .bt3-has-error .bt3-form-control:focus {
      border-color: #983d3b;
      box-shadow: inset 0 1px 1px rgb(0 0 0 / 7.5%), 0 0 6px #c16361;
    }
  `,
};

/**
 * Similar to InputFormGroup, but uses a Braintree Hosted Field instead of the actual input.
 * https://developers.braintreepayments.com/guides/hosted-fields/overview/javascript/v3
 * As such, the exported component connects to the Fluxible store to access the instance of HostedFields created by
 * BrintreeForm.
 */
export class BraintreeHostedFieldFormGroup extends React.Component<PropsToComponent, State> {
  static defaultProps = {
    cols: '7',
  };

  constructor(props: PropsToComponent) {
    super(props);
    this.state = {
      valid: false,
      potentiallyValid: true,
      focused: false,
      attempted: false,
    };
  }

  componentDidMount() {
    this.subscribeToBraintreeEvents();
  }

  componentDidUpdate(prevProps: Readonly<PropsToComponent>): void {
    const previousBraintreeHostedFields = prevProps.braintreeHostedFields;
    if (previousBraintreeHostedFields !== this.props.braintreeHostedFields) {
      this.subscribeToBraintreeEvents();
    }
    if (!prevProps.isFormSubmitted && this.props.isFormSubmitted) {
      const { braintreeHostedFields } = this.props;
      if (braintreeHostedFields) {
        braintreeHostedFields.addClass(this.props.braintreeFieldName, 'form-submitted');
      }
    }
  }

  addBraintreeHandlers(
    eventNames: Exclude<HostedFieldEventType, 'binAvailable'>[] | Exclude<HostedFieldEventType, 'binAvailable'>,
    handler: (fieldState: HostedFieldsHostedFieldsFieldData) => void
  ) {
    const { braintreeHostedFields } = this.props;
    if (!braintreeHostedFields) {
      throw new Error('only call addBraintreeHandler when there is an instance of HostedFields available');
    }
    const eventNameList = typeof eventNames === 'string' ? [eventNames] : eventNames;
    eventNameList.forEach((eventName) => {
      try {
        braintreeHostedFields.on(eventName, (event) => {
          const { emittedBy } = event;
          const { braintreeFieldName } = this.props;
          if (emittedBy !== braintreeFieldName) {
            return;
          }
          const fieldState = event.fields[braintreeFieldName];
          handler(fieldState);
        });
      } catch (braintreeError) {
        Sentry.captureMessage(braintreeError.message, { extra: { braintreeError } });
      }
    });
  }

  subscribeToBraintreeEvents() {
    const { braintreeHostedFields, onStatusChange } = this.props;
    const { onBlur, onFocus, onEmpty, onNotEmpty, onCardTypeChange, onValidityChange, onInputSubmitRequest } =
      this.props;

    if (!braintreeHostedFields) {
      return;
    }
    this.addBraintreeHandlers('validityChange', (fieldState) => {
      if (fieldState.isValid) {
        this.setState(() => ({
          valid: true,
          potentiallyValid: true,
        }));
      } else if (fieldState.isPotentiallyValid) {
        this.setState(() => ({
          valid: false,
          potentiallyValid: true,
        }));
      } else {
        this.setState(() => ({
          valid: false,
          potentiallyValid: false,
        }));
      }
      onStatusChange();
    });
    this.addBraintreeHandlers(['focus', 'blur'], (fieldState) => {
      // set "focused" state
      this.setState(() => ({
        focused: fieldState.isFocused,
      }));

      // set "attempted" state
      if (!fieldState.isFocused && !fieldState.isEmpty) {
        // If we're leaving a field and we've entered something, this is considered an "attempt".
        // Note that we never "un-attempt" (set `attempted` to false), so if you later clear out a field and leave it,
        // we still consider it "attempted" (and show an error).
        this.setState(() => ({
          attempted: true,
        }));
      }

      // set focused state CSS
      // We need to mark the field as focused or not, so that our CSS will show the field value as red for an invalid,
      // unfocused field.
      if (fieldState.isFocused) {
        braintreeHostedFields.removeClass(this.props.braintreeFieldName, 'blurry');
        // Only mark it as unfocused if we've made an attempt (otherwise empty field placeholders will show as red after
        // they're visited but unmodified).
      } else if (this.state.attempted) {
        braintreeHostedFields.addClass(this.props.braintreeFieldName, 'blurry');
      }
    });
    this.addBraintreeHandlers(['empty', 'notEmpty'], onStatusChange);

    // add external event handler callbacks
    if (onBlur) {
      this.addBraintreeHandlers('blur', onBlur);
    }

    if (onFocus) {
      this.addBraintreeHandlers('focus', onFocus);
    }

    if (onEmpty) {
      this.addBraintreeHandlers('empty', onEmpty);
    }

    if (onNotEmpty) {
      this.addBraintreeHandlers('notEmpty', onNotEmpty);
    }

    if (onCardTypeChange) {
      this.addBraintreeHandlers('cardTypeChange', onCardTypeChange);
    }

    if (onValidityChange) {
      this.addBraintreeHandlers('validityChange', onValidityChange);
    }

    if (onInputSubmitRequest) {
      this.addBraintreeHandlers('inputSubmitRequest', onInputSubmitRequest);
    }
  }

  render() {
    const { labelText, braintreeFieldName, isFormSubmitted, isVerticallyAligned, hideErrorIcon, cols, subLabelText } =
      this.props;
    const placeholderId = braintreeFieldIds[braintreeFieldName];

    const { attempted, valid, potentiallyValid, focused } = this.state;
    // Only show an error when we're certain that the user has finished typing (they've exited the field after typing
    // something or clicked Submit), or when there's no chance of it being correct with further typing.
    const hasError = (((attempted && !focused) || isFormSubmitted) && !valid) || !potentiallyValid;

    const formGroupClass = classnames({
      'bt3-form-group': !isVerticallyAligned,
      'bt3-has-success': valid,
      'bt3-has-error': hasError,
    });

    const hostedFieldPlaceholder = (
      <div
        className="bt-placeholder padded-input"
        id={placeholderId}
        data-e2e={placeholderId}
        data-testid={placeholderId}
      />
    );

    return (
      <div css={styles.formContainer}>
        <div className="rc-BraintreeHostedFieldFormGroup" data-testid="braintree-hosted-field-form-group">
          {isVerticallyAligned ? (
            <div className={formGroupClass}>
              {labelText && (
                <label className="label-text" htmlFor={placeholderId}>
                  {labelText}
                  {subLabelText && <span className="label-text__sub-label">{subLabelText}</span>}
                  {!hideErrorIcon && <InvalidInputIcon hasError={hasError} />}
                </label>
              )}
              {hostedFieldPlaceholder}
            </div>
          ) : (
            <div className={formGroupClass}>
              <div className="bt3-col-sm-1" />
              <label className="bt3-col-sm-3" htmlFor={placeholderId}>
                <b className="body-2-text">{labelText}</b>
                <InvalidInputIcon hasError={hasError} />
              </label>
              <div className={`bt3-col-sm-${cols}`}>{hostedFieldPlaceholder}</div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

type Stores = {
  PaymentFormStore: PaymentFormStoreType;
};

export default connectToStores<PropsToComponent, PropsFromCaller, Stores>(
  ['PaymentFormStore'],
  ({ PaymentFormStore }) => {
    return {
      braintreeHostedFields: PaymentFormStore.getBraintreeHostedFields(),
      onStatusChange: () => PaymentFormStore.emitChange(),
    };
  }
)(BraintreeHostedFieldFormGroup);
